import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Newspaper, CalendarDays, Images, UserPlus, LogOut, Users } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NewsTab } from "@/components/admin/NewsTab";
import { EventsTab } from "@/components/admin/EventsTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { RegistrationsTab } from "@/components/admin/RegistrationsTab";
import logo3c from "@/assets/logo-3c-jatim.jpg";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (user === null)
    return <div className="grid min-h-screen place-items-center bg-[#090A0F] text-slate-400">Memeriksa sesi...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const cards = [
    { icon: Newspaper, label: "Berita", value: stats?.news ?? "-", testid: "stat-news" },
    { icon: CalendarDays, label: "Event", value: stats?.events ?? "-", testid: "stat-events" },
    { icon: Images, label: "Foto Galeri", value: stats?.gallery ?? "-", testid: "stat-gallery" },
    { icon: UserPlus, label: "Pendaftar Baru", value: stats?.registrations_pending ?? "-", testid: "stat-pending" },
  ];

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#090A0F]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
          <div className="flex items-center gap-3">
            <img
              src={logo3c}
              alt="Logo 3C Jatim"
              className="h-10 w-10 rounded-full object-cover border border-white/20 shadow-md shadow-rose-600/30"
            />
            <div>
              <p className="font-heading text-xl font-bold uppercase leading-none text-white">Dashboard Admin</p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          <Button data-testid="admin-logout-button" onClick={doLogout} variant="outline" size="sm"
            className="border-white/15 text-slate-300 hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-400">
            <LogOut className="mr-2 h-4 w-4" /> Keluar
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-8 px-4 py-10 sm:px-6 lg:px-12">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.testid} data-testid={`admin-${c.testid}`}
              className="rounded-xl border border-white/10 bg-[#12141C]/90 p-5">
              <c.icon className="h-5 w-5 text-amber-400" />
              <p className="mt-3 font-heading text-3xl font-bold text-white">{c.value}</p>
              <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{c.label}</p>
            </div>
          ))}
        </div>
        <Tabs defaultValue="pendaftaran" className="space-y-6">
          <TabsList className="flex h-auto flex-wrap gap-1 border border-white/10 bg-[#12141C]/90 p-1">
            <TabsTrigger data-testid="admin-tab-kelola-pendaftaran" value="pendaftaran"
              className="data-[state=active]:bg-rose-600 data-[state=active]:text-white">
              <Users className="mr-2 h-4 w-4" /> Pendaftaran
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-kelola-berita" value="berita"
              className="data-[state=active]:bg-rose-600 data-[state=active]:text-white">
              <Newspaper className="mr-2 h-4 w-4" /> Berita
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-kelola-event" value="event"
              className="data-[state=active]:bg-rose-600 data-[state=active]:text-white">
              <CalendarDays className="mr-2 h-4 w-4" /> Event
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-kelola-galeri" value="galeri"
              className="data-[state=active]:bg-rose-600 data-[state=active]:text-white">
              <Images className="mr-2 h-4 w-4" /> Galeri
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pendaftaran"><RegistrationsTab /></TabsContent>
          <TabsContent value="berita"><NewsTab /></TabsContent>
          <TabsContent value="event"><EventsTab /></TabsContent>
          <TabsContent value="galeri"><GalleryTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
